import { useRef } from "react";
import { useQuery } from "@connectrpc/connect-query";
import { keepPreviousData } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

import { formatNumber } from "@yatayat/core/money";
import { ConsoleService } from "@yatayat/gen/yatayat/console/v1/console_pb";

import { ComplianceEvidence } from "~/components/console/ComplianceEvidence";
import { FilterSelect } from "~/components/console/ComplianceKit";
import { sortKeys, type SortDir, type SortKey } from "~/components/console/complianceSort";
import { ComplianceTable, ScoreLegend } from "~/components/console/ComplianceTable";
import { useStaffSessionExpiry } from "~/components/OperatorTrip";
import { QueryError } from "~/components/QueryState";
import { useI18n } from "~/lib/i18n";
import { retryTransient } from "~/lib/rpc/errors";
import { searchString } from "~/lib/search";
import { ui } from "~/lib/ui";

type OperatorsSearch = { period?: number; sort?: SortKey; dir?: SortDir; operator?: string };

const periods = [30, 90, 180, 365] as const;
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const defaultPeriod = 90;

// The period, sort and open operator live in the URL, so going back from the complaints restores them.
export const Route = createFileRoute("/console/operators")({
  validateSearch: (search: Record<string, unknown>): OperatorsSearch => {
    const period = Number(search.period);
    const sort = searchString(search.sort);
    const dir = searchString(search.dir);
    return {
      period: periods.some((p) => p === period) ? period : undefined,
      sort: sortKeys.find((k) => k === sort),
      dir: dir === "asc" || dir === "desc" ? dir : undefined,
      operator: searchString(search.operator),
    };
  },
  component: ConsoleOperators,
});

function ConsoleOperators() {
  const { t, lang } = useI18n();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const evidenceHeading = useRef<HTMLHeadingElement>(null);

  const period = search.period ?? defaultPeriod;
  // Lowest score first: the operators that need attention.
  const sort = search.sort ?? "score";
  const dir = search.dir ?? "asc";

  const compliance = useQuery(
    ConsoleService.method.listOperatorCompliance,
    { periodDays: period },
    { retry: retryTransient, placeholderData: keepPreviousData },
  );
  const unauthenticated = useStaffSessionExpiry(compliance.error);
  const scores = compliance.data?.operators ?? [];
  const operatorId = search.operator && uuid.test(search.operator) ? search.operator : undefined;
  const selected = scores.find((s) => s.operatorId === operatorId);

  function openEvidence(operatorId: string) {
    // Once the panel renders, so a keyboard user lands on the evidence.
    void navigate({ search: (prev) => ({ ...prev, operator: operatorId }), replace: true, resetScroll: false }).then(() =>
      requestAnimationFrame(() => evidenceHeading.current?.focus()),
    );
  }

  function closeEvidence() {
    const closing = operatorId;
    // Back to the operator the evidence was opened from.
    void navigate({ search: (prev) => ({ ...prev, operator: undefined }), replace: true, resetScroll: false }).then(() =>
      requestAnimationFrame(() => document.querySelector<HTMLElement>(`[data-operator="${closing}"]`)?.focus()),
    );
  }

  function choosePeriod(value: string) {
    const days = Number(value);
    void navigate({ search: (prev) => ({ ...prev, period: days === defaultPeriod ? undefined : days }), replace: true, resetScroll: false });
  }

  function sortBy(key: SortKey, nextDir: SortDir) {
    // The default order stays out of the URL.
    const byDefault = key === "score" && nextDir === "asc";
    void navigate({ search: (prev) => ({ ...prev, sort: byDefault ? undefined : key, dir: byDefault ? undefined : nextDir }), replace: true, resetScroll: false });
  }

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h1 className={ui.h1}>{t("consoleComplianceTitle")}</h1>
        <p className={ui.muted}>{t("consoleComplianceIntro")}</p>
      </div>

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="w-56">
          <FilterSelect
            label={t("consoleCompliancePeriod")}
            value={String(period)}
            onChange={choosePeriod}
            options={periods.map((p) => ({ value: String(p), label: t("consoleComplianceLastDays", { n: formatNumber(p, lang) }) }))}
          />
        </div>
        <ScoreLegend />
      </div>

      <div aria-busy={compliance.isFetching} className="space-y-2">
        {compliance.isPending && <p className={ui.muted}>{t("loading")}</p>}
        {compliance.isError && !compliance.data && !unauthenticated && (
          <QueryError onRetry={() => void compliance.refetch()} busy={compliance.isFetching} />
        )}
        {compliance.data && scores.length === 0 && <p className={ui.empty}>{t("consoleComplianceNone")}</p>}
        {scores.length > 0 && (
          <>
            <p className={ui.muted}>{t("consoleComplianceSortHint")}</p>
            <div className={compliance.isPlaceholderData ? "opacity-60" : undefined}>
              <ComplianceTable
                scores={scores}
                sort={sort}
                dir={dir}
                onSort={sortBy}
                selectedId={operatorId}
                onSelect={openEvidence}
              />
            </div>
          </>
        )}
      </div>

      {operatorId && (selected || !compliance.data) && (
        <ComplianceEvidence
          key={operatorId}
          operatorId={operatorId}
          operatorName={selected?.operatorName?.[lang] ?? ""}
          periodDays={period}
          headingRef={evidenceHeading}
          onClose={closeEvidence}
        />
      )}
    </div>
  );
}
